import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["cannotPublishOnDomainReason", "errors", "warnings", "canSchedule", "publishEnabled", "unpublishEnabled", "testVariationTooltipPropsForApproval", "extraDropdownActions", "shouldOverrideDefaultButtonTextKey", "buttonTextKey", "updateFocusedItemAction", "tooltipOverride", "isLocked", "onClickLocked", "shouldConfirmPublishAction", "shouldUseCustomPublishHandler", "customPublishHandler", "publish", "unpublish", "schedulePublishUnpublish", "isNotAuthorizedToPublish"],
  _excluded2 = ["publishDropdownActions", "approvalDropdownActions", "isPublishEnabled", "isPublishInProgress", "loadingFailed", "publishErrors", "publishWarnings", "errorsAndWarningsCount", "onIssueClick", "handleMouseEnterButton", "handleTrackOnDropdownCompleteAction", "openModal", "closeModal"];
import EPIssueButton from 'editor-components/header/EPIssueButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import EditorApprovalButtons from '../approvals/EditorApprovalButtons';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUnifiedPublishType } from '../../redux/selectors/publishSelectors';
import UILockBadge from 'UIComponents/badge/UILockBadge';
// @ts-expect-error Untyped module
import { updatePublishImmediately } from '../../redux/actions/publishActions';
import { PublishTypes } from '../../constants/PublishTypes';
import { PublishActionModalNames } from '../publishing/types/publishButtonTypes';
import useEditorPublishProps from '../publishing/hooks/useEditorPublishProps';
import EditorPublishButtonActionModals from '../publishing/modals/EditorPublishButtonActionModals';
import useContentCrmObject from '../publishing/hooks/useContentCrmObject';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue } from 'content-management-data-lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EditorPublishButton = _ref => {
  let {
      cannotPublishOnDomainReason,
      errors,
      warnings,
      canSchedule,
      publishEnabled,
      unpublishEnabled,
      testVariationTooltipPropsForApproval = {
        isTestVariation: false,
        testVariationDisabledTooltip: undefined
      },
      extraDropdownActions,
      shouldOverrideDefaultButtonTextKey = false,
      buttonTextKey,
      updateFocusedItemAction,
      tooltipOverride,
      isLocked = false,
      onClickLocked = () => {},
      shouldConfirmPublishAction,
      shouldUseCustomPublishHandler,
      customPublishHandler,
      publish,
      unpublish,
      schedulePublishUnpublish,
      isNotAuthorizedToPublish = false
    } = _ref,
    publishModalProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const _useEditorPublishProp = useEditorPublishProps({
      canSchedule,
      cannotPublishOnDomainReason,
      isLocked,
      extraDropdownActions,
      unpublishEnabled,
      publishEnabled,
      errors,
      warnings,
      updateFocusedItemAction
    }),
    {
      publishDropdownActions,
      approvalDropdownActions,
      isPublishEnabled,
      isPublishInProgress,
      loadingFailed,
      publishErrors,
      publishWarnings,
      errorsAndWarningsCount,
      onIssueClick,
      handleMouseEnterButton,
      handleTrackOnDropdownCompleteAction,
      openModal,
      closeModal
    } = _useEditorPublishProp,
    publishActionModalProps = _objectWithoutPropertiesLoose(_useEditorPublishProp, _excluded2);
  const {
    contentCrmObjectPublishType,
    canScheduleWithContentCrmObject,
    isContentCrmObjectLoading,
    contentCrmObject
  } = useContentCrmObject();
  const unifiedPublishType = useSelector(getUnifiedPublishType);
  const finalButtonTextKey = useMemo(() => {
    if (canScheduleWithContentCrmObject && contentCrmObjectPublishType && !shouldOverrideDefaultButtonTextKey // this is required for overriding the publish button text in staged pages editor
    ) {
      if (isNotAuthorizedToPublish) {
        return `header.publishButton.notAuthorized.label`;
      }
      return `header.publishButton.${contentCrmObjectPublishType}.label`;
    }
    return buttonTextKey;
  }, [buttonTextKey, canScheduleWithContentCrmObject, contentCrmObjectPublishType, isNotAuthorizedToPublish, shouldOverrideDefaultButtonTextKey]);
  const dispatch = useDispatch();
  const handlePublishButtonClick = useCallback(() => {
    if (isLocked) {
      onClickLocked();
      return;
    }
    if (shouldUseCustomPublishHandler && customPublishHandler) {
      customPublishHandler();
      return;
    }
    if (!contentCrmObject && unifiedPublishType !== PublishTypes.schedule) {
      dispatch(updatePublishImmediately(true));
    }
    if (shouldConfirmPublishAction) {
      openModal(PublishActionModalNames.PUBLISH_CONFIRM);
      return;
    }
    if (canScheduleWithContentCrmObject && contentCrmObjectPublishType === PublishTypes.schedule && schedulePublishUnpublish && contentCrmObject) {
      schedulePublishUnpublish({
        schedulePublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE)
      });
      return;
    }
    publish(contentCrmObjectPublishType || unifiedPublishType);
  }, [canScheduleWithContentCrmObject, contentCrmObject, contentCrmObjectPublishType, customPublishHandler, dispatch, isLocked, onClickLocked, openModal, publish, schedulePublishUnpublish, shouldConfirmPublishAction, shouldUseCustomPublishHandler, unifiedPublishType]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EditorApprovalButtons, Object.assign({
      errorsAndWarningsCount: errorsAndWarningsCount,
      onOpenWarningsAndSuggestionsPanel: () => openModal(PublishActionModalNames.WARNINGS_AND_SUGGESTIONS),
      buttonSize: "sm",
      dropdownProps: {
        buttonSize: 'sm'
      },
      extraActionButtons: approvalDropdownActions
    }, testVariationTooltipPropsForApproval, {
      children: /*#__PURE__*/_jsx("div", {
        "data-onboarding": "onboarding-publish-button",
        "data-campaign-assistant-onboarding-tour": "tour-step-publish",
        children: /*#__PURE__*/_jsxs(EPIssueButton, {
          actions: publishDropdownActions,
          "data-test": "publish-issue-button",
          errors: publishErrors,
          warnings: publishWarnings,
          tooltipOverride: tooltipOverride,
          onButtonClick: handlePublishButtonClick,
          onDropdownOpenComplete: handleTrackOnDropdownCompleteAction,
          onIssueClick: onIssueClick,
          onMouseEnter: handleMouseEnterButton,
          disabled: !isPublishEnabled || isContentCrmObjectLoading,
          loading: isPublishInProgress,
          loadingFailed: loadingFailed,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: finalButtonTextKey
          }), isLocked ? /*#__PURE__*/_jsx(UILockBadge, {}) : null]
        })
      })
    })), /*#__PURE__*/_jsx(EditorPublishButtonActionModals, Object.assign({
      publishErrors: publishErrors,
      publishWarnings: publishWarnings,
      buttonTextKey: finalButtonTextKey,
      onIssueClick: onIssueClick,
      publish: publish,
      unpublish: unpublish,
      openModal: openModal,
      closeModal: closeModal,
      schedulePublishUnpublish: schedulePublishUnpublish
    }, publishModalProps, publishActionModalProps))]
  });
};
export default EditorPublishButton;